import { QueryClient } from '@tanstack/react-query'
import { ApiError } from './api'
import type { RequestFilter } from './types'

/** Central key registry so invalidation after a mutation is never guesswork. */
export const qk = {
  me: ['auth', 'me'] as const,
  sessions: ['auth', 'sessions'] as const,
  status: ['status'] as const,
  account: ['account'] as const,
  referral: ['account', 'referral'] as const,
  requests: (status: RequestFilter) => ['requests', status] as const,
  request: (id: number) => ['requests', 'detail', id] as const,
  downloads: ['downloads'] as const,
  notifications: (unreadOnly: boolean) => ['notifications', { unreadOnly }] as const,
  notificationPreferences: ['notifications', 'preferences'] as const,
  pushVapidKey: ['push', 'vapid'] as const,
  invitePreview: (code: string) => ['invite', code] as const,
  adminUsers: ['admin', 'users'] as const,
  adminInvites: ['admin', 'invites'] as const,
  adminProfiles: ['admin', 'profiles'] as const,
  adminLibraries: ['admin', 'libraries'] as const,
  adminActivity: (offset: number) => ['admin', 'activity', offset] as const,
} as const

/** Poll interval for anything with a live progress bar. */
/** Poll cadence while something is actively downloading. */
export const LIVE_POLL_MS = 5_000

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Every poll is billed Worker traffic, so nothing refetches in a
        // background tab and nothing retries a decision the server already made.
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
        // Deliberately 0: a 20s stale window silently throttled the 5s live
        // poll, so progress bars appeared to update at a quarter of their
        // configured rate. Freshness is controlled by refetchInterval alone.
        staleTime: 0,
        gcTime: 10 * 60_000,
        retry: (failureCount, error) => {
          if (error instanceof ApiError) {
            // 4xx is an answer; a downed home server will not recover inside a
            // retry loop either — the UI shows the snapshot instead.
            if (error.status >= 400 && error.status < 500) return false
            if (error.status === 503) return false
          }
          return failureCount < 2
        },
      },
      mutations: {
        retry: false,
      },
    },
  })
}
